import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import {
  Company,
  Package,
  PaymentMethodEnum,
  HeadsetListItem,
  WorkingModeEnum,
} from 'src/models';
import { RoleEnum } from 'src/security/roles';
import { HEADSETS, HOTELS } from 'src/constants';
import {
  compareString,
  currencySign,
  daysFromToday,
  formatShortDate,
  formatWorkingMode,
  getLastActivityColor,
  hybridCompare,
  uuidV4,
} from 'src/helper';
import { useContextData, usePage, useLanguage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import {
  clearHeadsetsState,
  clearActivationCode,
} from 'src/store/reducers/headsets';
import { clearBatteryState } from 'src/store/reducers/battery';
import { clearReportsHeadsetsState } from 'src/store/reducers/reportHeadsets';
import { useAccount } from 'src/services/auth';
import { useCompanyById } from 'src/services/company';
import { usePackages, fetchPackagesAction } from 'src/services/packages';
import {
  useHeadsetActivationCode,
  useHeadsets,
  useHeadsetsState,
  activateHeadsetSubscriptionAction,
  cancelHeadsetActiveSubscriptionAction,
  fetchHeadsetActivationCodeAction,
  fetchHeadsetsAction,
} from 'src/services/headsets';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  useStickyCols,
  BatteryStatus,
  CardStatus,
} from 'src/components';
import {
  Button,
  Modal,
  GroupFilter,
  useSearchInput,
  FilterModal,
  FilterModalButton,
  Tooltip,
  ClipboardController,
  FilterModalIndicator,
  Link,
} from 'src/components/general';
import { ReactComponent as CrownIcon } from 'src/icons/crown.svg';
import { ReactComponent as TooltipIcon } from 'src/icons/tooltip.svg';
import { HeadsetsInfoBar, PackageCard } from './hotelHeadsets.components';
import styles from './hotelHeadsetsStyle.module.scss';

type HeadsetFilter = 'withProblems' | 'inTestMode';

export const HotelHeadsets = () => {
  const {
    header: { width: headerWidth },
    navigator: { setBackUrl },
  } = useContextData();
  const { hotel: companyId } = useParams() as { hotel: string };
  const dispatch = useAppDispatch();
  const { t, td } = useLanguage();
  const {
    access: { accessMap },
  } = usePage();

  const filterList: { id: HeadsetFilter; label: string }[] = useMemo(
    () => [
      {
        id: 'withProblems',
        label: t('page.hotel.headsets.headsetFilter.withProblems'),
      },
      {
        id: 'inTestMode',
        label: t('page.hotel.headsets.headsetFilter.inTestMode'),
      },
    ],
    [t],
  );
  const [filterSet, setFilterSet] = useState<Set<HeadsetFilter>>(new Set());
  const [filterSetModal, setFilterSetModal] = useState<Set<HeadsetFilter>>(
    new Set(filterSet),
  );

  const activationCode = useHeadsetActivationCode();
  const state = useHeadsetsState();
  const headsets = useHeadsets();
  const company = useCompanyById(companyId) as Company | null;
  const packages = usePackages();
  const account = useAccount();

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const [isLoading, setIsLoading] = useState(state.isPending);
  const [isFiltered, setIsFiltered] = useState(false);
  const [selectedHeadset, setSelectedHeadset] = useState<
    HeadsetListItem | undefined
  >(undefined);
  const [activatePkgData, setActivatePkgData] = useState<{
    headsetId: string;
    package: Package;
  } | null>(null);
  const [cancelSubscriptionData, setCancelSubscriptionData] = useState<{
    headsetId: string;
    package: Package;
  } | null>(null);

  const filteredHeadsets = useMemo(() => {
    let list = [...headsets];

    if (filterSet.has('withProblems')) {
      list = list.filter(
        (headset) =>
          (!headset.charging && headset.battery && headset.battery <= 15) ||
          headset.cardState !== 'ok' ||
          (!headset.alive && daysFromToday(headset.lastActivity) > 3) ||
          headset.isTest,
      );
    }

    if (filterSet.has('inTestMode')) {
      list = list.filter(({ isTest }) => isTest);
    }

    return list;
  }, [headsets, filterSet]);

  const {
    searchString,
    searchInput,
    modalSearchInput,
    applySearch,
    cancelSearch,
    clearSearch,
    focusInput,
  } = useSearchInput({
    inputClassName: styles.searchInput,
    modalInputClassName: styles.modalSearchInput,
    disabled: isLoading,
    onEnter: () => {
      setShowSearchModal(false);
    },
  });

  useEffect(() => {
    if (showSearchModal) {
      focusInput();
    }
  }, [showSearchModal, focusInput]);

  const packagesForActivation = useMemo(
    () => packages.filter((p) => !p.vipAccess),
    [packages],
  );
  const headsetPackageActivationModalTitle = useMemo(() => {
    return t(
      selectedHeadset?.roomLabel
        ? 'page.hotel.headsets.packageActivationTitleWithRoom'
        : 'page.hotel.headsets.packageActivationTitle',
      {
        label: selectedHeadset?.label || '-',
        roomLabel: selectedHeadset?.roomLabel || '-',
      },
    );
  }, [t, selectedHeadset?.label, selectedHeadset?.roomLabel]);

  const isSubscriptionActivationAvailable = useCallback(
    (headset: HeadsetListItem) => {
      if (account?.role === RoleEnum.engineer && !headset.isTest) {
        return false;
      }

      return (
        company?.paymentMethods.includes(PaymentMethodEnum.reception) &&
        (headset.workingMode === WorkingModeEnum.reception ||
          headset.workingMode === WorkingModeEnum.inRoom)
      );
    },
    [account?.role, company?.paymentMethods],
  );

  const handleCloseSubscriptionModal = useCallback(() => {
    setSelectedHeadset(undefined);
  }, [setSelectedHeadset]);

  const handleDeviceActivationClick = useCallback(async () => {
    await dispatch(fetchHeadsetActivationCodeAction({ companyId }));
  }, [dispatch, companyId]);

  const handleSubscriptionActivate = useCallback(
    async (headsetId: string, packageId: string) => {
      const res = await dispatch(
        activateHeadsetSubscriptionAction({
          companyId,
          headsetId,
          data: { packageId },
        }),
      );
      if (activateHeadsetSubscriptionAction.fulfilled.match(res)) {
        handleCloseSubscriptionModal();
      }
    },
    [dispatch, companyId, handleCloseSubscriptionModal],
  );

  const handleCancelActiveSubscription = useCallback(
    async (headsetId: string) => {
      const res = await dispatch(
        cancelHeadsetActiveSubscriptionAction({ companyId, headsetId }),
      );
      if (cancelHeadsetActiveSubscriptionAction.fulfilled.match(res)) {
        setCancelSubscriptionData(null);
      }
    },
    [dispatch, companyId],
  );

  const enableSubscribe = useMemo(
    () =>
      account?.role &&
      [RoleEnum.admin, RoleEnum.reception, RoleEnum.engineer].includes(
        account?.role,
      ) &&
      company?.paymentMethods.includes(PaymentMethodEnum.reception),
    [account?.role, company?.paymentMethods],
  );

  const reloadPackages = useCallback(
    async (companyId: string) => {
      if (!company || !enableSubscribe) {
        return;
      }

      await dispatch(fetchPackagesAction({ companyId }));
    },
    [dispatch, company, enableSubscribe],
  );

  const isQueryChanged = useMemo(() => Boolean(searchString), [searchString]);

  const loadHeadsets = useCallback(
    (withoutIndication?: boolean) => {
      if (!company) {
        return;
      }

      if (!withoutIndication) {
        setIsLoading(true);
      }
      dispatch(
        fetchHeadsetsAction({
          companyId,
          query: searchString || undefined,
          withoutIndication,
          uuid: uuidV4(),
        }),
      );
    },
    [company, companyId, searchString, dispatch],
  );

  useEffect(() => {
    loadHeadsets();

    const id = setInterval(() => {
      (async () => await loadHeadsets(true))();
    }, 15000);
    return () => clearInterval(id);
  }, [loadHeadsets]);

  useEffect(() => {
    if (state.isPending === false) {
      setIsLoading(false);
    }
  }, [state.isPending]);

  useEffect(() => {
    setIsFiltered(isQueryChanged || Boolean(filterSet.size));
  }, [isQueryChanged, filterSet.size]);

  const resetFilter = useCallback(() => {
    if (isQueryChanged) {
      setIsLoading(true);
    }

    clearSearch();
    setFilterSet(new Set());
    setIsFiltered(false);
  }, [clearSearch, isQueryChanged]);

  useEffect(() => {
    reloadPackages(companyId);
  }, [companyId, reloadPackages]);

  const closeActivationCodeModal = useCallback(() => {
    dispatch(clearActivationCode());
  }, [dispatch]);

  useEffect(
    () => () => {
      dispatch(clearHeadsetsState());
      dispatch(clearBatteryState());
    },
    [dispatch],
  );

  const config: StickyTableConfig<HeadsetListItem>[] = useMemo(() => {
    const result: StickyTableConfig<HeadsetListItem>[] = [
      {
        title: t('page.hotel.headsets.label'),
        key: 'label',
        field: 'label',
        sorter: (a, b, sortOrder) =>
          hybridCompare(a.label, b.label) * (sortOrder === 'descend' ? -1 : 1),
        render: (label, headset) => {
          return (
            <div
              className={cl(
                styles.label,
                !accessMap[HEADSETS].length && styles.inactive,
              )}
            >
              <div>
                <Link
                  className={styles.title}
                  src={
                    accessMap[HEADSETS].length
                      ? `/${HOTELS}/${companyId}/${HEADSETS}/${headset.id}/${accessMap[HEADSETS][0]}`
                      : null
                  }
                  onClick={() => {
                    if (!accessMap[HEADSETS].length) {
                      return;
                    }

                    dispatch(clearReportsHeadsetsState());
                    setBackUrl(null);
                  }}
                >
                  <span>{label || '-'}</span>{' '}
                  {headset.hasActiveSubscriptions && <CrownIcon />}
                </Link>
                <div className={styles.secondary}>
                  {headset.firmware || '-'}
                  {'-'}
                  {headset.deviceType || t('general.unknown')}
                  <Tooltip
                    text={[
                      t('tooltip.headsetId', { id: headset.phyDeviceId }),
                      t('tooltip.humanId', { id: headset.humanId }),
                    ]}
                    tooltipClassName={styles.tooltip}
                    arrowClassName={styles.tooltipArrow}
                    withClipboard
                  >
                    <TooltipIcon />
                  </Tooltip>
                </div>
              </div>
              <div className={styles.tags}>
                {headset.hasActiveSession && headset.alive && (
                  <div className={cl(styles.tag, styles.green)}>
                    {t('general.inUse')}
                  </div>
                )}
                {headset.isTest && (
                  <div className={cl(styles.tag, styles.blue)}>
                    {t('general.test')}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: t('page.hotel.headsets.workingMode'),
        key: 'workingMode',
        field: 'workingMode',
        sorter: (a, b, sortOrder) =>
          compareString(a.workingMode, b.workingMode) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => <div>{td(formatWorkingMode(value))}</div>,
      },
      {
        title: t('page.hotel.headsets.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        sorter: (a, b, sortOrder) =>
          compareString(a.roomLabel, b.roomLabel) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => (
          <div className={styles.roomLabel}>{value || '-'}</div>
        ),
      },
      {
        title: t('page.hotel.headsets.cardState'),
        key: 'cardState',
        field: 'cardState',
        sorter: (a, b, sortOrder) =>
          (Number(a.cardState === 'ok') - Number(b.cardState === 'ok')) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (label: string, headset) => (
          <CardStatus
            cardState={headset.cardState}
            onlyWarning
            style={{ marginTop: 8 }}
          />
        ),
      },
      {
        title: t('page.hotel.headsets.battery'),
        key: 'battery',
        field: 'battery',
        sorter: (a, b, sortOrder) => {
          const left = a.battery || -1;
          const right = b.battery || -1;
          return (left - right) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, headset) => (
          <BatteryStatus
            battery={value}
            charging={headset.charging}
            className={styles.tag120}
          />
        ),
      },
      {
        title: t('page.hotel.headsets.lastActivity'),
        key: 'lastActivity',
        field: 'lastActivity',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.lastActivity).getTime();
          const dateB = new Date(b.lastActivity).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, { alive }) =>
          alive ? (
            <div className={cl(styles.tag, styles.tag120, styles.green)}>
              {t('page.hotel.headsets.online')}
            </div>
          ) : (
            <div
              className={cl(
                styles.tag,
                styles.tag120,
                styles[getLastActivityColor(value)],
              )}
            >
              {formatShortDate(value)}
            </div>
          ),
      },
      {
        title: t('page.hotel.headsets.lastPayment'),
        key: 'lastPayment',
        field: 'lastPayment',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.lastPayment).getTime();
          const dateB = new Date(b.lastPayment).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value) => (
          <div
            className={cl(
              styles.tag,
              styles.tag120,
              daysFromToday(value) > 7 ? styles.pink : styles.gray,
            )}
          >
            {formatShortDate(value)}
          </div>
        ),
      },
    ];

    if (enableSubscribe) {
      result.push({
        title: t('page.hotel.headsets.subscription'),
        key: 'subscription',
        field: 'subscription',
        render: (value, headset) =>
          isSubscriptionActivationAvailable(headset) ? (
            headset.activeSubscriptions.length > 0 ? (
              <Button
                label={t('page.hotel.headsets.disable')}
                onClick={() => {
                  const activeSubscriptionId =
                    headset.activeSubscriptions[0].packageId;
                  const activePackage = packagesForActivation.find(
                    ({ id }) => id === activeSubscriptionId,
                  );
                  if (activePackage) {
                    setCancelSubscriptionData({
                      headsetId: headset.id,
                      package: activePackage,
                    });
                  }
                }}
                design="pink"
                autoSize
                className={styles.subscriptionButton}
              />
            ) : (
              <Button
                label={t('page.hotel.headsets.connect')}
                onClick={() => {
                  setSelectedHeadset(headset);
                }}
                design="white"
                autoSize
                className={styles.subscriptionButton}
              />
            )
          ) : null,
      });
    }

    return result;
  }, [
    t,
    td,
    enableSubscribe,
    accessMap,
    companyId,
    isSubscriptionActivationAvailable,
    packagesForActivation,
    dispatch,
    setBackUrl,
  ]);

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalGroupFilter,
  });

  const countsPerPages = useMemo(() => [50, 100, 200, Infinity], []);

  return (
    <div className={styles.container}>
      <FilterModal
        show={showSearchModal}
        close={() => {
          setShowSearchModal(false);
        }}
        onApply={() => {
          applySearch();
          setShowSearchModal(false);
        }}
        onCancel={() => {
          cancelSearch();
          setShowSearchModal(false);
        }}
        isSearch
      >
        {modalSearchInput}
      </FilterModal>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          setFilterSet(new Set(filterSetModal));
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          setFilterSetModal(new Set());
          resetSCChanges();
        }}
      >
        <GroupFilter
          label={t('page.hotel.headsets.headsetFilter')}
          selectedIDs={filterSetModal}
          items={filterList}
          selectItem={(id, value) => {
            setFilterSetModal((prevState) => {
              const set = new Set(prevState);
              if (value) {
                set.add(id as HeadsetFilter);
              } else {
                set.delete(id as HeadsetFilter);
              }
              return set;
            });
          }}
          className={styles.filterSelectModal}
          size="small"
        />
        {modalStickyColsFilter}
      </FilterModal>
      <Modal
        header={t('page.hotel.headsets.activationCodeTitle')}
        className={styles.modalActivationCode}
        show={Boolean(activationCode)}
        close={closeActivationCodeModal}
        footer={
          <>
            <Button
              label={t('general.close')}
              onClick={closeActivationCodeModal}
              design="lightBlue"
              autoSize
              className={styles.modalCloseButton}
            />
          </>
        }
        closeOnEsc
      >
        <div className={styles.modalContent}>
          <ClipboardController text={activationCode}>
            <div className={styles.activationCode}>{activationCode}</div>
          </ClipboardController>
        </div>
      </Modal>
      <Modal
        header={headsetPackageActivationModalTitle}
        className={styles.packagesModal}
        show={Boolean(selectedHeadset)}
        close={handleCloseSubscriptionModal}
        footer={
          <>
            <Button
              label={t('general.cancel')}
              onClick={handleCloseSubscriptionModal}
              design="outlineBlue"
              autoSize
              className={styles.modalPackageCancelButton}
            />
          </>
        }
        closeOnEsc
      >
        <div className={styles.packagesModalContent}>
          <div className={styles.packageCards}>
            {packagesForActivation.map((pkg) => (
              <PackageCard
                key={`pkg-${pkg.id}`}
                package={pkg}
                currency={company?.location?.currency || ''}
                activate={() => {
                  if (selectedHeadset) {
                    setActivatePkgData({
                      headsetId: selectedHeadset.id,
                      package: pkg,
                    });
                  }
                }}
              />
            ))}
          </div>
        </div>
      </Modal>
      <Modal
        show={Boolean(activatePkgData)}
        close={() => {
          setActivatePkgData(null);
        }}
        header={t('page.hotel.headsets.activateConfirm', {
          price: `${activatePkgData?.package.price} ${currencySign(company?.location?.currency)}`,
        })}
        className={styles.confirmModal}
        footer={
          <div className={styles.confirmModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setActivatePkgData(null);
              }}
              design="outlineBlue"
              autoSize
              className={cl(styles.modalButton, styles.cancelButton)}
            />
            <Button
              label={t('general.apply')}
              onClick={() => {
                setActivatePkgData(null);
                if (activatePkgData) {
                  handleSubscriptionActivate(
                    activatePkgData.headsetId,
                    activatePkgData.package.id,
                  );
                }
              }}
              design="lightBlue"
              autoSize
              className={styles.modalButton}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.confirmModalContent}>
          {t('page.hotel.headsets.activateConfirm.desc', {
            name: activatePkgData?.package.name,
          })}
        </div>
      </Modal>
      <Modal
        header={t('page.hotel.headsets.cancelSubscriptionTitle')}
        className={styles.cancelSubsModal}
        show={Boolean(cancelSubscriptionData)}
        close={() => {
          setCancelSubscriptionData(null);
        }}
        footer={
          <div className={styles.cancelSubsModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setCancelSubscriptionData(null);
              }}
              design="outlineBlue"
              autoSize
              className={styles.closeCancelSubsBtn}
            />
            <Button
              label={t('page.hotel.headsets.disable')}
              onClick={() => {
                if (cancelSubscriptionData) {
                  handleCancelActiveSubscription(
                    cancelSubscriptionData.headsetId,
                  );
                }
              }}
              design="pink"
              autoSize
              className={styles.cancelSubsModalBtn}
              disabled={Boolean(state.isPending)}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.cancelSubsModalContent}>
          {cancelSubscriptionData && (
            <PackageCard
              package={cancelSubscriptionData.package}
              currency={company?.location?.currency || ''}
            />
          )}
        </div>
      </Modal>
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HOTELS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowSearchModal}
              icon="search"
              badge={Boolean(searchString)}
              disabled={isLoading}
            />
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(filterSet.size)}
              disabled={isLoading}
            />{' '}
            <FilterModalIndicator
              icon="search"
              text={searchString}
              clear={clearSearch}
              disabled={isLoading}
            />
          </div>
        </div>
        <HeadsetsInfoBar
          headsets={headsets}
          role={account?.role}
          linkHeadset={handleDeviceActivationClick}
        />
        <div className={styles.filters}>
          <GroupFilter
            label={t('page.hotel.headsets.headsetFilter')}
            selectedIDs={filterSet}
            items={filterList}
            selectItem={(id, value) => {
              setFilterSet((prevState) => {
                const set = new Set(prevState);
                if (value) {
                  set.add(id as HeadsetFilter);
                } else {
                  set.delete(id as HeadsetFilter);
                }
                setFilterSetModal(new Set(set));
                return set;
              });
            }}
            className={styles.filterSelect}
          />
          {searchInput}
          {stickyColsFilter}
        </div>
      </div>
      <StickyTable
        config={account && company ? config : null}
        data={filteredHeadsets as unknown as HeadsetListItem[]}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
        countsPerPages={countsPerPages}
        initPerPage={100}
        isFiltered={isFiltered}
        resetFilter={resetFilter}
        notFoundText={t('general.resetFilters')}
      />
    </div>
  );
};

export default HotelHeadsets;
